'use client'

import * as SliderPrimitive from '@radix-ui/react-slider'
import { type ComponentProps, useCallback, useEffect, useRef, useState } from 'react'

import { cn } from '../../utils/cn'

const DEFAULT_MIN_GAP = 3
const KEYBOARD_TOOLTIP_TIMEOUT_MS = 1000
const KEYBOARD_STEP_SIZE = 5
const SHIFT_STEP_SIZE = 25
const ARROW_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'])

type RangeSliderProps = Omit<
  ComponentProps<typeof SliderPrimitive.Root>,
  'value' | 'onValueChange'
> & {
  value?: [number, number]
  onValueChange?: (value: [number, number]) => void
  minGap?: number
}

function RangeSlider({
  className,
  value = [0, 100],
  onValueChange,
  minGap = DEFAULT_MIN_GAP,
  disabled,
  ...props
}: RangeSliderProps) {
  const [showKeyboardTooltip, setShowKeyboardTooltip] = useState<number | null>(null)
  const [hoveringThumb, setHoveringThumb] = useState<number | null>(null)
  const keyboardTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleValueChange = useCallback(
    (values: number[]) => {
      if (values.length !== 2) return

      let [min, max] = values as [number, number]

      // Enforce minimum gap
      if (max - min < minGap) {
        // Determine which thumb moved and adjust the other
        if (min !== value[0]) {
          // Left thumb moved - push right thumb if needed
          max = Math.min(100, min + minGap)
        } else {
          // Right thumb moved - push left thumb if needed
          min = Math.max(0, max - minGap)
        }
      }

      onValueChange?.([min, max])
    },
    [onValueChange, minGap, value]
  )

  const handleKeyDown = useCallback(
    (thumbIndex: number) => (e: React.KeyboardEvent) => {
      if (!ARROW_KEYS.has(e.key)) return

      setShowKeyboardTooltip(thumbIndex)

      if (keyboardTooltipTimeoutRef.current) {
        clearTimeout(keyboardTooltipTimeoutRef.current)
      }
      keyboardTooltipTimeoutRef.current = setTimeout(() => {
        setShowKeyboardTooltip(null)
      }, KEYBOARD_TOOLTIP_TIMEOUT_MS)

      // Always handle keyboard navigation manually to enforce custom step sizes
      e.preventDefault()
      const isIncrement = e.key === 'ArrowRight' || e.key === 'ArrowUp'
      const stepSize = e.shiftKey ? SHIFT_STEP_SIZE : KEYBOARD_STEP_SIZE
      const step = isIncrement ? stepSize : -stepSize
      const newValues: [number, number] = [...value]

      // Calculate new position while maintaining minimum gap between thumbs:
      // - Left thumb (0): constrained between 0 and (right thumb position - minGap)
      // - Right thumb (1): constrained between (left thumb position + minGap) and 100
      if (thumbIndex === 0) {
        newValues[0] = Math.max(0, Math.min(value[1] - minGap, value[0] + step))
      } else {
        newValues[1] = Math.min(100, Math.max(value[0] + minGap, value[1] + step))
      }

      onValueChange?.(newValues)
    },
    [value, onValueChange, minGap]
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (keyboardTooltipTimeoutRef.current) {
        clearTimeout(keyboardTooltipTimeoutRef.current)
      }
    }
  }, [])

  return (
    <SliderPrimitive.Root
      data-slot="range-slider"
      className={cn('relative flex w-full touch-none items-center select-none', className)}
      value={value}
      onValueChange={handleValueChange}
      step={1}
      minStepsBetweenThumbs={minGap}
      disabled={disabled}
      {...props}
    >
      <SliderPrimitive.Track className="bg-accent relative h-2 w-full grow overflow-hidden rounded-full">
        <SliderPrimitive.Range
          className={cn('absolute h-full', disabled ? 'bg-primary/40' : 'bg-primary')}
        />
      </SliderPrimitive.Track>
      {value.map((thumbValue, index) => {
        const shouldShowTooltip = hoveringThumb === index || showKeyboardTooltip === index

        return (
          <SliderPrimitive.Thumb
            key={index}
            className="bg-primary-foreground ring-offset-background focus-visible:ring-ring border-border relative block size-[18px] cursor-grab rounded-full border-[0.5px] shadow-md transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none active:cursor-grabbing disabled:pointer-events-none disabled:opacity-50"
            onMouseEnter={() => setHoveringThumb(index)}
            onMouseLeave={() => setHoveringThumb(null)}
            onKeyDown={handleKeyDown(index)}
            style={{ touchAction: 'none' }}
          >
            {shouldShowTooltip && (
              <div className="bg-card absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 rounded-md border px-2 py-1 text-xs shadow-md">
                {thumbValue}%
              </div>
            )}
          </SliderPrimitive.Thumb>
        )
      })}
    </SliderPrimitive.Root>
  )
}

export { RangeSlider }
export type { RangeSliderProps }
